import React from 'react';
import TypeaheadSelect from './TypeaheadSelect';
import GradeMultiSelect from './GradeMultiSelect';
import { SizeTag, Button } from '../styles/CustomerSpecsIndexStyles';
import { defaultGradesForG } from '../utils/tieredGrades';

// Ordered Style / Label / Method selector, extracted from the Customer Specs Form
// and Manage panels (which carried byte-identical copies). Props:
//
//   value          array of selected values. In the default (flat) mode this is a
//                  scalar id array; in gradeMode it is Array<{ value, grades:[names] }>.
//   onChange(next) receives the new value array
//   options        full [{ value, label }] list (the component excludes already-selected)
//   coerce         Number (Style/Method) or String (Label) — drives add coercion,
//                  dedupe, and remove/compare so behavior matches the originals exactly
//   displayFor(v)  -> chip / value text for a selected value
//   pinnedValue / pinnedLabel  Label only: a special option (NO_PLU) kept at the TOP
//                  of the add dropdown whenever it isn't already selected. It is NOT
//                  exclusive — it coexists with real values and is added/graded like one.
//   headerNote     optional note rendered above the input
//   renderFooter() optional page-owned content below the list (PLU / bag-image previews)
//
//   gradeMode      OFF (default) => exact flat-array behavior, rows are SizeTag + ▲▼/Remove.
//                  ON => entries are { value, grades }, rows are the split pill + GradeMultiSelect.
//   specGrades     the spec's current grade NAMES (spec order); drives the default-by-G rule
//                  and the GradeMultiSelect options. Only consumed in gradeMode.
//   bagLabelContextFor(value) / onBagLabelsChange(value, nextWire)
//                  Styles instance only: per-entry bag-label context + wire-form setter
//                  forwarded to GradeMultiSelect. When absent (Label/Method), entries and
//                  rows are byte-identical to the pre-bag behavior.

const NOTE_STYLE = { fontSize: '0.85rem', color: 'var(--shadow-a55)', marginBottom: 4 };

export default function TieredGradeList({
  value = [],
  onChange,
  options = [],
  coerce = (v) => v,
  displayFor,
  pinnedValue,
  pinnedLabel,
  headerNote,
  renderFooter,
  addAriaLabel = 'Add item',
  addPlaceholder = 'Add...',
  gradeMode = false,
  specGrades = [],
  bagLabelContextFor,
  onBagLabelsChange,
}) {
  const list = Array.isArray(value) ? value : [];
  const hasPinned = pinnedValue !== undefined;
  // In gradeMode each entry is { value, grades }; otherwise the entry IS the scalar id.
  const entryVal = (e) => (gradeMode && e && typeof e === 'object' ? e.value : e);
  const has = (v) => list.some((x) => coerce(entryVal(x)) === coerce(v));

  const dropdownOptions = (() => {
    const opts = options.filter((o) => !has(o.value));
    // Keep the pinned option (NO_PLU) at the TOP of the list until it's selected.
    if (hasPinned && !has(pinnedValue)) opts.unshift({ value: pinnedValue, label: pinnedLabel });
    return opts;
  })();

  const handleAdd = (val) => {
    const raw = String(val ?? '').trim();
    if (!raw) return;
    const added = coerce(raw);
    if (typeof added === 'number' && !Number.isFinite(added)) return;
    if (gradeMode) {
      if (list.some((x) => coerce(entryVal(x)) === coerce(added))) { onChange(list); return; }
      const entry = { value: added, grades: defaultGradesForG(specGrades) };
      if (bagLabelContextFor) entry.bagLabels = null; // fresh style follows Default
      onChange([...list, entry]);
      return;
    }
    onChange(Array.from(new Set([...list, added])));
  };

  const move = (idx, delta) => {
    const arr = [...list];
    const j = idx + delta;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    onChange(arr);
  };

  const remove = (v) => onChange(list.filter((x) => coerce(entryVal(x)) !== coerce(v)));

  const setEntryGrades = (entryValue, nextGrades) =>
    onChange(list.map((x) => (coerce(entryVal(x)) === coerce(entryValue) ? { ...x, grades: nextGrades } : x)));

  const renderRow = (entry, idx) => {
    const v = entryVal(entry);
    const controls = (
      <>
        <Button type="button" onClick={() => move(idx, -1)}>▲</Button>
        <Button type="button" onClick={() => move(idx, 1)}>▼</Button>
        <Button type="button" onClick={() => remove(v)} style={{ background: 'var(--grey-500)' }}>Remove</Button>
      </>
    );
    if (!gradeMode) {
      return (
        <div key={`${v}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SizeTag style={{ background: 'var(--grey-700)' }}>{displayFor ? displayFor(v) : v}</SizeTag>
          {controls}
        </div>
      );
    }
    return (
      <div key={`${v}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <GradeMultiSelect
          valueLabel={displayFor ? displayFor(v) : v}
          grades={specGrades}
          selected={Array.isArray(entry?.grades) ? entry.grades : []}
          onChange={(next) => setEntryGrades(v, next)}
          bagContext={bagLabelContextFor ? bagLabelContextFor(v) : null}
          onBagLabelsChange={onBagLabelsChange ? (nextWire) => onBagLabelsChange(v, nextWire) : undefined}
        />
        {controls}
      </div>
    );
  };

  return (
    <>
      {headerNote ? <div style={NOTE_STYLE}>{headerNote}</div> : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <TypeaheadSelect
          ariaLabel={addAriaLabel}
          value=""
          placeholder={addPlaceholder}
          size="lg"
          options={dropdownOptions}
          onChange={handleAdd}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {list.map((entry, idx) => renderRow(entry, idx))}
        </div>
        {renderFooter ? renderFooter() : null}
      </div>
    </>
  );
}
